import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import { connect } from "react-redux";

import { DateFormat, FoodType } from "../../../modules/constants";
import SmartPageHeader from "../../../components/smart/PageHeader/PageHeader";
import CustomModal, {
  ModalSize,
} from "../../../components/ui-library/CustomModal/CustomModal";
import DailyEntries from "../../../components/smart/DailyEntries/DailyEntries";
import DietDiary from "../../../components/smart/DietDiary/DietDiary";
import {
  DietDairyTypes,
  dietDairyTypeToString,
  getGlycosadeNutritionItems,
} from "../../../components/smart/DietDiary/DietDiary.constants";
import TopPageButtons from "../../../components/library/TopPageButtons/TopPageButtons";
import { DailyListKeys } from "../../../components/smart/DailyEntries/Constants";

import {
  dailyEntries,
  mapDispatchToProps,
  mapStateToProps,
} from "../../../components/smart/DailyEntries/DailyEntries.store";
import FavoriteMeals from "../../../components/smart/FavoriteMeals/FavoriteMeals";
import { useTranslation } from "react-i18next";

const ParticipantLanding = (props) => {
  const {
    yesterdayEntries,
    todayEntries,
    fetchEntries,
    route,
    studies,
    dailyTargets,
  } = props;

  const { t } = useTranslation();

  const [selectedDietDiaryOption, setSelectedDietDiaryOption] = useState(null);
  const [dietDiaryModalTitle, setDietDiaryModalTitle] = useState("");

  const [todayDate, setTodayDate] = useState(new Date());
  const [isVisible, setIsVisible] = useState();
  const onVisibilityChange = () => {
    setIsVisible(!document.hidden);
  };

  const dailyGoalsDate = {
    label: t("appText.today"),
    key: DailyListKeys.todayListName,
    date: moment(),
    formatedDate: moment().format(DateFormat.EXTENDED_FORMAT),
  };

  const dailyGoalsDateYesterday = {
    label: t("appText.yesterday"),
    key: DailyListKeys.yesterdayListName,
    date: moment().subtract(1, "days"),
    formatedDate: moment()
      .subtract(1, "days")
      .format(DateFormat.EXTENDED_FORMAT),
  };

  useEffect(() => {
    window.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const refreshData = useCallback(() => {
    fetchEntries(dailyGoalsDate);
    fetchEntries(dailyGoalsDateYesterday);
  }, [fetchEntries, dailyGoalsDate, dailyGoalsDateYesterday]);

  useEffect(() => {
    if (isVisible && !moment(todayDate).isSame(moment(new Date()), "day")) {
      setTodayDate(new Date());
      refreshData();
    }
  }, [isVisible, refreshData, todayDate]);

  const selectDietDiaryOption = (option) => {
    if (option.id === dietDiaryOptions.FAVORITES.id) {
      setDietDiaryModalTitle(t("appText.dietDiary.addFavorite"));
    } else {
      setDietDiaryModalTitle(`${t("appText.add")} ${option.text}`);
    }

    setSelectedDietDiaryOption(option);
  };

  const dietDiaryOptions = {
    FAVORITES: {
      id: "1",
      text: t("appText.favorites"),
      onClick: () => {
        selectDietDiaryOption(dietDiaryOptions.FAVORITES);
      },
    },
    FOOD_DRINK: {
      id: "3",
      text: dietDairyTypeToString(DietDairyTypes.FOOD_DRINK),
      type: DietDairyTypes.FOOD_DRINK,
      onClick: () => {
        selectDietDiaryOption(dietDiaryOptions.FOOD_DRINK);
      },
    },
    CORNSTARCH: {
      id: "2",
      text: dietDairyTypeToString(DietDairyTypes.CORNSTARCH),
      type: DietDairyTypes.CORNSTARCH,
      defaultItems: [
        {
          foodType: FoodType.BRANDED,
          nixItemId: "51d376c9cc9bff5553aa95ee",
          canRemove: true,
        },
      ],
      onClick: () => {
        selectDietDiaryOption(dietDiaryOptions.CORNSTARCH);
      },
    },
    GLYCOSADE: {
      id: "4",
      text: dietDairyTypeToString(DietDairyTypes.GLYCOSADE),
      type: DietDairyTypes.GLYCOSADE,
      defaultItems: [
        {
          foodType: FoodType.CUSTOM,
          nutritionItems: getGlycosadeNutritionItems(),
        },
      ],
      onClick: () => {
        selectDietDiaryOption(dietDiaryOptions.GLYCOSADE);
      },
    },
  };

  const onCloseDietDiaryModal = () => {
    setSelectedDietDiaryOption(null);
    setDietDiaryModalTitle(null);

    refreshData();
  };

  const onSelectFavoriteMeal = (favoriteMeal) => {
    const type =
      JSON.parse(favoriteMeal.json)?.type ?? DietDairyTypes.FOOD_DRINK;

    setDietDiaryModalTitle(
      `${t("appText.add")} ${dietDairyTypeToString(type, t)}`
    );

    setSelectedDietDiaryOption({
      jsonData: favoriteMeal.json,
      type: type,
      favoriteId: favoriteMeal.favoriteId,
    });
  };

  useEffect(() => {
    if (!todayEntries) {
      fetchEntries(dailyGoalsDate);
    }
  }, [todayEntries, fetchEntries, dailyGoalsDate]);

  useEffect(() => {
    if (!yesterdayEntries) {
      fetchEntries(dailyGoalsDateYesterday);
    }
  }, [yesterdayEntries, fetchEntries, dailyGoalsDateYesterday]);

  return (
    <>
      {selectedDietDiaryOption && (
        <CustomModal
          show={selectedDietDiaryOption !== null}
          title={dietDiaryModalTitle ?? ""}
          size={ModalSize.large}
          onClose={() => onCloseDietDiaryModal()}
        >
          {selectedDietDiaryOption?.id === dietDiaryOptions.FAVORITES.id ? (
            <FavoriteMeals onSelectFavoriteMeal={onSelectFavoriteMeal} />
          ) : (
            <DietDiary
              defaultItems={selectedDietDiaryOption?.defaultItems}
              type={selectedDietDiaryOption?.type}
              dietDiaryOption={selectedDietDiaryOption}
              onSubmit={() => onCloseDietDiaryModal()}
              maxDate={moment().toDate()}
              minDate={moment().subtract(1, "days").toDate()}
              setModalTitle={setDietDiaryModalTitle}
              studyId={route.study}
              mealJsonData={selectedDietDiaryOption?.jsonData}
              baseFavoriteMealId={selectedDietDiaryOption?.favoriteId}
            />
          )}
        </CustomModal>
      )}

      <SmartPageHeader
        title={studies && studies[0]?.code}
        studyId={route.study}
        subtitle=""
        roleName={t("appText.participant")}
        shouldFetchParticipant={true}
      />
      <TopPageButtons buttons={Object.values(dietDiaryOptions)} />
      <DailyEntries
        date={dailyGoalsDate}
        goalsChartTitle={t(
          "contentText.dietDiary.macronutrientsPanel.today.title"
        )}
        key={dailyGoalsDate.key}
        entries={todayEntries}
        dailyGoals={dailyTargets}
        studyId={route.study}
        closeDietDiaryAction={() => onCloseDietDiaryModal()}
        refetchData={(date) => fetchEntries(date)}
      />
      <DailyEntries
        date={dailyGoalsDateYesterday}
        goalsChartTitle={t(
          "contentText.dietDiary.macronutrientsPanel.yesterday.title"
        )}
        listTitle={t("contentText.dietDiary.entriesPanel.yesterday.title")}
        key={dailyGoalsDateYesterday.key}
        entries={yesterdayEntries}
        dailyGoals={dailyTargets}
        studyId={route.study}
        closeDietDiaryAction={() => onCloseDietDiaryModal()}
        refetchData={(date) => fetchEntries(date)}
      />
    </>
  );
};

export default dynamic(
  "ParticipantLanding",
  subspaced({
    // We map extra state because we might need to acess other stores for data, subspace is self contained.
    mapExtraState: (state) => ({
      loginReducer: { ...state.loginReducer },
    }),
  }),
  attachReducer(dailyEntries)
)(connect(mapStateToProps, mapDispatchToProps)(ParticipantLanding));
